import { useEffect, useState } from "react";
import clsx from "clsx";

import { Button, Card, Skeleton, Snackbar, Tooltip } from "@chef/components";
import {
  BRAND_NAME,
  BRAND_NAME_FULL,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
  PAYMENT_METHODS,
  PAYMENT_PARTNER_IDS,
  PAYMENT_STATUS_IDS,
} from "@chef/constants";
import { formatDate, formatCurrency } from "@chef/helpers";
import {
  OrdersQuery,
  useBillingQuery,
  useLazyOrdersQuery,
} from "@chef/state-management";
import { Klarna, MobilePayBadge, VippsBadge } from "@chef/icons/other";
import { isEqualStrings } from "@chef/utils/equal";
import { usePaymentSession } from "@chef/state-management/hooks";

import { ReceiptDefault } from "@chef/icons/small";
import { AccountStatusWarning, Modal } from "@chef/smart-components";

import { MyAccountLayout } from "../../components/MyAccountLayout";

import { intl } from "./MyAccountPaymentMethodPage.Intl";

interface CardDetailsProps {
  last4?: string;
  expMonth?: number;
  expYear?: number;
}

interface PaymentMethodProps {
  paymentPartnerId?: string;
  tokenType?: string;
  onCheckout: () => void;
}

const PAYMENT_HISTORY_TO_LOAD_PER_CLICK = 4;

const PaymentMethod = ({
  paymentPartnerId,
  tokenType,
  onCheckout,
}: PaymentMethodProps) => {
  if (!paymentPartnerId) {
    return "";
  }

  if (isEqualStrings(paymentPartnerId, PAYMENT_PARTNER_IDS.COLLECTOR)) {
    return (
      <div>
        {intl.YOURE_PAYING_WITH} {intl.INVOICE}
      </div>
    );
  }

  // New vipps payment method
  if (isEqualStrings(paymentPartnerId, PAYMENT_PARTNER_IDS.VIPPS_MOBILEPAY)) {
    const icon =
      BRAND_NAME === "RN" ? (
        <MobilePayBadge className="w-24 h-10 ml-1" />
      ) : (
        <VippsBadge className="w-10 h-10 ml-1" />
      );

    return (
      <div id="vipps-payment-method">
        {intl.YOURE_PAYING_WITH} {icon}
      </div>
    );
  }

  switch (tokenType) {
    case "scheme":
      return intl.PAYMENT_CARD;
    // Old vipps payment method
    case "vipps":
      return (
        <div id="old-vipps-payment-method">
          {intl.YOURE_PAYING_WITH} <VippsBadge className="w-10 h-10 ml-1" />
          <p className="mt-2">
            Du betaler med en utdatert versjon av Vipps. Denne versjonen blir
            faset ut og du må{" "}
            <button onClick={onCheckout} className="underline">
              oppdatere betalingsmåten din
            </button>{" "}
            så snart som mulig for å holde kontoen din aktiv.
          </p>
        </div>
      );
    case "klarna_paynow":
      return (
        <div>
          {intl.YOURE_PAYING_WITH} <Klarna className="h-10 ml-1 w-15" />
        </div>
      );
    default:
      return "";
  }
};

const getOrderPaymentStatus = (
  status?: string,
  price?: number,
  orderTypeId?: string,
) => {
  if (price === 0 || isEqualStrings(orderTypeId, ORDER_TYPE_IDS.GIFTCARD)) {
    return "-";
  }

  if (status === undefined) {
    return (
      <Tooltip
        element={
          <div className="w-48 p-2">
            {intl.THIS_ORDER_HAS_UNKNOWN_PAYMENT_STATUS}
          </div>
        }
        wrapperClassName="w-fit"
      >
        <span className="underline decoration-dotted decoration-black decoration-2 underline-offset-2">
          {intl.UNKNOWN} (?)
        </span>
      </Tooltip>
    );
  }

  if (status === PAYMENT_STATUS_IDS.PAID) {
    return intl.PAID;
  }

  if (status === PAYMENT_STATUS_IDS.DEBT_COLLECTION) {
    return <span className="text-error">{intl.SENT_TO_DEBT_COLLECTION}</span>;
  }

  return <span className="text-error">{intl.UNPAID}</span>;
};

const CardDetails = ({ last4, expMonth, expYear }: CardDetailsProps) => {
  if (!last4 || !expMonth || !expYear) {
    return null;
  }

  return (
    <div>
      <div>**** **** **** {last4}</div>
      <div>
        {intl.EXPIRATION_DATE}: {expMonth.toString().padStart(2, "0")}/
        {expYear?.toString()?.slice(2)}
      </div>
    </div>
  );
};

export const MyAccountPaymentMethodPage = () => {
  const [loadedOrderCount, setLoadedOrderCount] = useState(0);
  const [ordersNotCancelled, setOrdersNotCancelled] = useState<
    OrdersQuery["orders"]
  >([]);
  const [fetchedOrders, setFetchedOrders] = useState<OrdersQuery["orders"]>([]);

  const { data: billingQuery, isLoading: isLoadingBilling } = useBillingQuery(
    undefined,
    { refetchOnFocus: true },
  );

  const { createSession, isCreating: isCreatingSession } = usePaymentSession();

  const [fetchOrders, { isLoading, isUninitialized, isFetching }] =
    useLazyOrdersQuery();

  const isLoadingOrders = isLoading || isUninitialized || isFetching;

  const fetchMoreOrders = async () => {
    const { orders } = await fetchOrders({
      orderTypes: [],
      orderStatuses: [ORDER_STATUS_IDS.FINISHED, ORDER_STATUS_IDS.PROCESSING],
      count: PAYMENT_HISTORY_TO_LOAD_PER_CLICK,
      skip: loadedOrderCount,
    }).unwrap();

    setOrdersNotCancelled(
      orders.filter((order) => order.orderStatus !== "Cancelled"),
    );

    setFetchedOrders([...fetchedOrders, ...ordersNotCancelled]);
    setLoadedOrderCount(loadedOrderCount + PAYMENT_HISTORY_TO_LOAD_PER_CLICK);
  };

  useEffect(() => {
    fetchMoreOrders();
  }, []);

  const allOrdersAreLoaded =
    loadedOrderCount > [...fetchedOrders, ...ordersNotCancelled].length;

  const { last4, expMonth, expYear, tokenType } =
    billingQuery?.billing?.currentPaymentCard || {};

  const noOrders = [...fetchedOrders, ...ordersNotCancelled].length === 0;

  return (
    <>
      <Modal open={isCreatingSession} panel="card" size="sm">
        {intl.CREATING_PAYMENT_SESSION}
      </Modal>
      <MyAccountLayout page="paymentMethod">
        <div
          className="flex flex-col col-span-5 gap-6"
          id="min-konto-betaling-card-grid"
        >
          <AccountStatusWarning />
          <Card id="min-konto-betaling-payment-card">
            <div className="flex flex-wrap items-center justify-between">
              <h2 className="text-xl">
                <strong>{intl.PAYMENT_METHOD}</strong>
              </h2>

              <Button
                id="min-konto-betaling-change-payment-card-btn"
                onClick={createSession}
                loading={isCreatingSession}
                disabled={isLoadingBilling}
                outlined
              >
                {intl.CHANGE_BUTTON_TEXT}
              </Button>
            </div>

            <Snackbar showIcon={false} className="mt-3" warning>
              {isLoadingBilling ? (
                <div className="flex flex-col gap-2">
                  <Skeleton height={4} width={48} />
                  <Skeleton height={4} width={48} />
                </div>
              ) : (
                <>
                  <PaymentMethod
                    paymentPartnerId={billingQuery?.billing.paymentPartnerId}
                    tokenType={tokenType}
                    onCheckout={createSession}
                  />

                  {isEqualStrings(
                    billingQuery?.billing.paymentPartnerId,
                    PAYMENT_PARTNER_IDS.ADYEN,
                  ) && (
                    <div className="flex gap-4 mt-1 text-sm">
                      <CardDetails
                        last4={last4}
                        expMonth={expMonth}
                        expYear={expYear}
                      />
                    </div>
                  )}
                </>
              )}
            </Snackbar>
          </Card>

          <Card id="min-konto-betaling-account-statement-card">
            <h2 className="text-xl">
              <strong>{intl.PAYMENT_HISTORY}</strong>
            </h2>

            {noOrders && !isLoadingOrders && (
              <div className="py-2 text-sm text-grey-1">
                <p>{intl.NO_ORDERS}</p>
              </div>
            )}

            {noOrders && isLoadingOrders && (
              <div className="py-2 text-sm text-grey-1">
                <p>{intl.LOADING_ORDERS}</p>
              </div>
            )}

            {!noOrders && (
              <>
                <table className="w-full mt-3 text-left">
                  <thead>
                    <tr>
                      <th className="w-1 pb-4 pr-6 whitespace-nowrap">
                        <strong>{intl.DATE}</strong>
                      </th>
                      <th className="pb-4">
                        <strong>{intl.AMOUNT}</strong>
                      </th>
                      <th className="pb-4">
                        <strong>{intl.STATUS}</strong>
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {[...fetchedOrders, ...ordersNotCancelled].map((order) => {
                      const { paymentData, id } = order;

                      const isCC =
                        paymentData?.paymentPartnerInfo
                          .paymentPartnerMethodCode === PAYMENT_METHODS.CARD;

                      const url = new URL(
                        "/api/payment/downloadReceipt",
                        "https://www.example.com",
                      );
                      // HOTFIX - Remove after discussion with backend
                      if (isCC && paymentData?.invoiceNumber) {
                        url.searchParams.set(
                          "receiptId",
                          paymentData.invoiceNumber.toString(),
                        );
                      } else {
                        url.searchParams.set("invoiceId", id);
                      }

                      const isFinished = order.orderStatus === "Finished";
                      const paymentStatus = getOrderPaymentStatus(
                        paymentData?.paymentStatusId,
                        order.finalPrice,
                        order.orderTypeId,
                      );

                      const isInDebtCollection = isEqualStrings(
                        paymentData?.paymentStatusId,
                        PAYMENT_STATUS_IDS.DEBT_COLLECTION,
                      );

                      const showDownloadLink =
                        isFinished && !isInDebtCollection;

                      return (
                        <tr
                          key={order.orderId}
                          className="border-t border-grey-1"
                        >
                          {isLoadingOrders && isUninitialized ? (
                            <>
                              <td className="pr-4">
                                <Skeleton height={5} width={30} />
                              </td>
                              <td>
                                <Skeleton height={5} width={15} />
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="w-1 py-4 pr-6 whitespace-nowrap">
                                {formatDate(order.deliveryDate, "dd/MM-yyyy")}
                              </td>
                              <td className="px-2 py-4 whitespace-nowrap">
                                {formatCurrency(order.finalPrice)}{" "}
                                {intl.CURRENCY_UNIT}
                              </td>
                              <td>{paymentStatus}</td>
                            </>
                          )}

                          <td className="w-48 py-4 text-right">
                            {isInDebtCollection && (
                              <Tooltip
                                element={
                                  <div className="w-48">
                                    {intl.THIS_PAYMENT_IS_IN_DEBT_COLLECTION}
                                  </div>
                                }
                                wrapperClassName="w-fit ml-auto"
                              >
                                <span className="underline">
                                  <ReceiptDefault className="w-6 h-6" />
                                </span>
                              </Tooltip>
                            )}
                            {showDownloadLink && (
                              <a
                                className="underline"
                                href={`${url.pathname}${url.search}`}
                                download={`${BRAND_NAME_FULL} ${
                                  intl.RECEIPT
                                } ${formatDate(
                                  order.deliveryDate,
                                  "yyyy-MM-dd",
                                )}`}
                              >
                                <ReceiptDefault className="w-6 h-6" />
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div
                  className={clsx(
                    ordersNotCancelled.length === 0 && "hidden",
                    "flex justify-center",
                  )}
                >
                  <Button
                    onClick={fetchMoreOrders}
                    disabled={allOrdersAreLoaded}
                    loading={isLoadingOrders}
                    outlined
                    className="mt-8"
                  >
                    {intl.LOAD_MORE_BUTTON_TEXT}
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      </MyAccountLayout>
    </>
  );
};
